import { useState, useEffect, useCallback } from 'react'
import {
	Box,
	Button,
	Dialog,
	Drawer,
	IconButton,
	TextField,
	Alert,
	useMediaQuery,
	useTheme,
	Typography
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import { supabase } from "../utils/supabase"
import posthog, { isPostHogConfigured } from '../posthog'

function AddEditClientDialog({ open, onClose, merchant, editingClient = null, onClientSaved }) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	const [loading, setLoading] = useState(false)
	const [error, setError] = useState('')

	// Form state
	const [formData, setFormData] = useState({
		name: '',
		last_name: '',
		email: '',
		phone_number: '',
		address: '',
		address_2: '',
		city: '',
		state: '',
		zip: '',
		email_reminders: true,
		sms_reminders: true
	})

	// Phone number validation helper
	const validatePhoneNumber = (phone) => {
		if (!phone) return true // Phone is optional

		// Remove all non-digit characters
		const digitsOnly = phone.replace(/\D/g, '')

		// Check if it's exactly 10 digits
		return digitsOnly.length === 10
	}

	// Format phone number helper
	const formatPhoneNumber = (phone) => {
		if (!phone) return ''

		// Remove all non-digit characters
		const digitsOnly = phone.replace(/\D/g, '')

		// Format as (XXX) XXX-XXXX if 10 digits
		if (digitsOnly.length === 10) {
			return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`
		}

		return phone // Return original if not 10 digits
	}

	const resetForm = useCallback(() => {
		setFormData({
			name: '',
			last_name: '',
			email: '',
			phone_number: '',
			address: '',
			address_2: '',
			city: '',
			state: '',
			zip: '',
			email_reminders: true,
			sms_reminders: true
		})
		setError('')
	}, [setError])

	// Initialize form data when dialog opens
	useEffect(() => {
		if (open) {
			if (editingClient) {
				setFormData({
					name: editingClient.name || '',
					last_name: editingClient.last_name || '',
					email: editingClient.email || '',
					phone_number: editingClient.phone_number || '',
					address: editingClient.address || '',
					address_2: editingClient.address_2 || '',
					city: editingClient.city || '',
					state: editingClient.state || '',
					zip: editingClient.zip || '',
					email_reminders: editingClient.email_reminders !== undefined ? editingClient.email_reminders : true,
					sms_reminders: editingClient.sms_reminders !== undefined ? editingClient.sms_reminders : true
				})
			} else {
				resetForm()
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, editingClient?.id])

	const handleSubmit = async () => {
		// Validation
		if (!formData.name.trim()) {
			setError('First name is required')
			return
		}

		// Validate phone number if provided
		if (formData.phone_number.trim() && !validatePhoneNumber(formData.phone_number)) {
			setError('Phone number must be exactly 10 digits (e.g., 1234567890 or (123) 456-7890)')
			return
		}

		setLoading(true)
		setError('')

		try {
			let clientData

			if (editingClient) {
				// Update existing client
				const updateData = {
					name: formData.name.trim(),
					last_name: formData.last_name.trim() || null,
					email: formData.email.trim() || null,
					phone_number: formData.phone_number.trim() ? formatPhoneNumber(formData.phone_number.trim()) : null,
					address: formData.address.trim() || null,
					address_2: formData.address_2.trim() || null,
					city: formData.city.trim() || null,
					state: formData.state.trim() || null,
					zip: formData.zip.trim() || null,
					email_reminders: formData.email_reminders,
					sms_reminders: formData.sms_reminders
				}

				const { error } = await supabase
					.from('clients')
					.update(updateData)
					.eq('id', editingClient.id)

				if (error) throw error

				clientData = { ...editingClient, ...updateData }

			} else {
				// Create new client
				const insertData = {
					merchant_id: merchant.id,
					name: formData.name.trim(),
					last_name: formData.last_name.trim() || null,
					email: formData.email.trim() || null,
					phone_number: formData.phone_number.trim() ? formatPhoneNumber(formData.phone_number.trim()) : null,
					address: formData.address.trim() || null,
					address_2: formData.address_2.trim() || null,
					city: formData.city.trim() || null,
					state: formData.state.trim() || null,
					zip: formData.zip.trim() || null,
					email_reminders: formData.email_reminders,
					sms_reminders: formData.sms_reminders
				}

				const { data, error } = await supabase
					.from('clients')
					.insert(insertData)
					.select()
					.single()

				if (error) throw error

				clientData = data
			}

			if (!editingClient && isPostHogConfigured) {
				posthog.capture('client_created')
			}

			// Notify parent component
			if (onClientSaved) {
				onClientSaved({
					client: clientData,
					pets: [], // No pets in this dialog anymore
					isEdit: !!editingClient
				})
			}

			handleClose()

		} catch (e) {
			console.error('Error saving client:', e)
			setError(e.message || 'Failed to save client')
		} finally {
			setLoading(false)
		}
	}

	const handleFormChange = (field) => (event) => {
		let value = event.target.value

		// Format phone number as user types
		if (field === 'phone_number') {
			// Remove all non-digit characters
			const digitsOnly = value.replace(/\D/g, '')

			// Limit to 10 digits
			if (digitsOnly.length <= 10) {
				// Format based on length
				if (digitsOnly.length >= 6) {
					value = `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`
				} else if (digitsOnly.length >= 3) {
					value = `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`
				} else {
					value = digitsOnly
				}
			} else {
				// Don't allow more than 10 digits
				return
			}
		}

		setFormData(prev => ({
			...prev,
			[field]: value
		}))
	}

	const handleClose = () => {
		resetForm()
		onClose()
	}

	// Mobile Drawer — Nami "New/Edit client" bottom sheet
	const renderMobileDrawer = () => (
		<Drawer
			anchor="bottom"
			open={open}
			onClose={handleClose}
			PaperProps={{
				sx: {
					bgcolor: '#FFFFFF',
					borderRadius: '22px 22px 0 0',
					boxShadow: '0px -12px 40px rgba(46, 42, 64, 0.24)',
					maxHeight: '92vh',
					display: 'flex',
					flexDirection: 'column'
				}
			}}
		>
			{/* Grab handle */}
			<Box sx={{ width: '40px', height: '4px', borderRadius: '99px', bgcolor: '#DCD6EB', margin: '10px auto 4px', flex: 'none' }} />

			{/* Header */}
			<Box sx={{
				flex: 'none',
				display: 'flex',
				alignItems: 'flex-start',
				justifyContent: 'space-between',
				gap: '11px',
				padding: '10px 16px 12px 16px',
				borderBottom: '1px solid #E2E8F0'
			}}>
				<Box sx={{ minWidth: 0, flex: 1 }}>
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 800,
						fontSize: '17px',
						letterSpacing: '-0.015em',
						color: '#2D3748'
					}}>
						{editingClient ? 'Edit client' : 'New client'}
					</Typography>
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 550,
						fontSize: '12px',
						color: '#718096',
						whiteSpace: 'nowrap',
						overflow: 'hidden',
						textOverflow: 'ellipsis'
					}}>
						{editingClient ? 'Update contact details & reminders' : 'Pets can be added next'}
					</Typography>
				</Box>
				<IconButton
					onClick={handleClose}
					disabled={loading}
					size="small"
					aria-label="Close"
					sx={{ color: '#A0AEC0', '&:hover': { bgcolor: '#F7FAFC', color: '#4A5568' } }}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</Box>

			{/* Body */}
			<Box sx={{
				flex: 1,
				minHeight: 0,
				overflowY: 'auto',
				display: 'flex',
				flexDirection: 'column',
				gap: '18px',
				padding: '14px 16px 18px'
			}}>
				{error && (
					<Alert severity="error">
						{error}
					</Alert>
				)}

				{/* Contact */}
				<Box>
					<SectionLabel>Contact</SectionLabel>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
						<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
							<FormField
								label="First name"
								value={formData.name}
								onChange={handleFormChange('name')}
								placeholder="First name"
								required
							/>
							<FormField
								label="Last name"
								value={formData.last_name}
								onChange={handleFormChange('last_name')}
								placeholder="Last name"
							/>
						</Box>
						<FormField
							label="Email"
							type="email"
							value={formData.email}
							onChange={handleFormChange('email')}
							placeholder="Example@email.com"
						/>
						<FormField
							label="Phone number"
							value={formData.phone_number}
							onChange={handleFormChange('phone_number')}
							placeholder="(xxx) xxx-xxxx"
						/>
					</Box>
				</Box>

				{/* Address */}
				<Box>
					<SectionLabel>Address</SectionLabel>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
						<FormField
							label="Address line 1"
							value={formData.address}
							onChange={handleFormChange('address')}
							placeholder="2500 wilshire blvd"
						/>
						<FormField
							label="Address line 2"
							value={formData.address_2}
							onChange={handleFormChange('address_2')}
							placeholder="Unit 614"
						/>
						<Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px' }}>
							<FormField
								label="City"
								value={formData.city}
								onChange={handleFormChange('city')}
								placeholder="Los Angeles"
							/>
							<FormField
								label="State/Province"
								value={formData.state}
								onChange={handleFormChange('state')}
								placeholder="CA"
							/>
							<FormField
								label="Zip"
								value={formData.zip}
								onChange={handleFormChange('zip')}
								placeholder="90057"
							/>
						</Box>
					</Box>
				</Box>

				{/* Reminders */}
				<Box>
					<SectionLabel>Reminders</SectionLabel>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
						<ReminderCard
							icon={<MailOutlineIcon sx={{ fontSize: 16 }} />}
							label="Email reminders"
							sub="Confirmations & visit reminders"
							checked={formData.email_reminders}
							onToggle={() => setFormData(prev => ({ ...prev, email_reminders: !prev.email_reminders }))}
						/>
						<ReminderCard
							icon={<ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />}
							label="Text (SMS) reminders"
							sub="Day-before & on-my-way texts"
							checked={formData.sms_reminders}
							onToggle={() => setFormData(prev => ({ ...prev, sms_reminders: !prev.sms_reminders }))}
						/>
					</Box>
				</Box>
			</Box>

			{/* Footer */}
			<Box sx={{
				flex: 'none',
				display: 'flex',
				gap: '10px',
				padding: '12px 16px 28px',
				borderTop: '1px solid #E2E8F0'
			}}>
				<Button
					onClick={handleClose}
					disabled={loading}
					fullWidth
					sx={{
						p: '12px 16px',
						bgcolor: '#FFFFFF',
						border: '1px solid #CBD5E0',
						borderRadius: '10px',
						color: '#9F7AEA',
						fontFamily: 'Inter',
						fontWeight: 600,
						fontSize: '14px',
						textTransform: 'none',
						'&:hover': { bgcolor: '#F7FAFC', border: '1px solid #CBD5E0' }
					}}
				>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					disabled={loading}
					fullWidth
					sx={{
						p: '12px 16px',
						bgcolor: '#E9D8FD',
						border: '1px solid #9F7AEA',
						borderRadius: '10px',
						color: '#2D3748',
						fontFamily: 'Inter',
						fontWeight: 600,
						fontSize: '14px',
						textTransform: 'none',
						'&:hover': { bgcolor: '#E9D8FD', border: '1px solid #9F7AEA' }
					}}
				>
					{loading ? 'Saving...' : 'Save'}
				</Button>
			</Box>
		</Drawer>
	)

	// Desktop Modal
	const renderDesktopModal = () => (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth={false}
			PaperProps={{
				sx: {
					width: '640px',
					maxWidth: '92vw',
					borderRadius: '16px',
					boxShadow: '0px 24px 60px rgba(45, 55, 72, 0.24)'
				}
			}}
		>
			{/* Header */}
			<Box sx={{
				display: 'flex',
				alignItems: 'flex-start',
				justifyContent: 'space-between',
				gap: '12px',
				p: '20px 24px',
				borderBottom: '1px solid #E2E8F0'
			}}>
				<Box>
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 600,
						fontSize: '20px',
						lineHeight: '1.3em',
						color: '#2D3748'
					}}>
						{editingClient ? 'Edit client' : 'New client'}
					</Typography>
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 400,
						fontSize: '13px',
						color: '#718096',
						mt: '2px'
					}}>
						{editingClient
							? `Update ${formData.name || 'their'}’s contact details and reminder settings`
							: 'Add their contact details — pets can be added next'}
					</Typography>
				</Box>
				<IconButton
					onClick={handleClose}
					disabled={loading}
					size="small"
					aria-label="Close"
					sx={{
						color: '#A0AEC0',
						'&:hover': { bgcolor: '#F7FAFC', color: '#4A5568' }
					}}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</Box>

			{/* Content Section */}
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: '22px',
				p: '22px 24px',
				maxHeight: '70vh',
				overflow: 'auto'
			}}>
				{error && (
					<Alert severity="error">
						{error}
					</Alert>
				)}

				{/* Contact */}
				<Box>
					<SectionLabel>Contact</SectionLabel>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
						<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
							<FormField
								label="First name"
								value={formData.name}
								onChange={handleFormChange('name')}
								placeholder="First name"
								required
							/>
							<FormField
								label="Last name"
								value={formData.last_name}
								onChange={handleFormChange('last_name')}
								placeholder="Last name"
							/>
						</Box>
						<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
							<FormField
								label="Email"
								type="email"
								value={formData.email}
								onChange={handleFormChange('email')}
								placeholder="Example@email.com"
							/>
							<FormField
								label="Phone number"
								value={formData.phone_number}
								onChange={handleFormChange('phone_number')}
								placeholder="(xxx) xxx-xxxx"
							/>
						</Box>
					</Box>
				</Box>

				{/* Address */}
				<Box>
					<SectionLabel>Address</SectionLabel>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
						<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
							<FormField
								label="Address line 1"
								value={formData.address}
								onChange={handleFormChange('address')}
								placeholder="2500 wilshire blvd"
							/>
							<FormField
								label="Address line 2"
								value={formData.address_2}
								onChange={handleFormChange('address_2')}
								placeholder="Unit 614"
							/>
						</Box>
						<Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '16px' }}>
							<FormField
								label="City"
								value={formData.city}
								onChange={handleFormChange('city')}
								placeholder="Los Angeles"
							/>
							<FormField
								label="State/Province"
								value={formData.state}
								onChange={handleFormChange('state')}
								placeholder="CA"
							/>
							<FormField
								label="Zip"
								value={formData.zip}
								onChange={handleFormChange('zip')}
								placeholder="90057"
							/>
						</Box>
					</Box>
				</Box>

				{/* Reminders */}
				<Box>
					<SectionLabel>Reminders</SectionLabel>
					<Box sx={{ display: 'flex', gap: '12px' }}>
						<ReminderCard
							icon={<MailOutlineIcon sx={{ fontSize: 16 }} />}
							label="Email reminders"
							sub="Confirmations & visit reminders"
							checked={formData.email_reminders}
							onToggle={() => setFormData(prev => ({ ...prev, email_reminders: !prev.email_reminders }))}
						/>
						<ReminderCard
							icon={<ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />}
							label="Text (SMS) reminders"
							sub="Day-before & on-my-way texts"
							checked={formData.sms_reminders}
							onToggle={() => setFormData(prev => ({ ...prev, sms_reminders: !prev.sms_reminders }))}
						/>
					</Box>
				</Box>
			</Box>

			{/* Footer Actions */}
			<Box sx={{
				display: 'flex',
				gap: '10px',
				p: '16px',
				borderTop: '1px solid #E2E8F0'
			}}>
				<Button
					onClick={handleClose}
					disabled={loading}
					fullWidth
					sx={{
						p: '12px 16px',
						bgcolor: '#FFFFFF',
						border: '1px solid #CBD5E0',
						borderRadius: '6px',
						color: '#9F7AEA',
						fontFamily: 'Inter',
						fontWeight: 600,
						fontSize: '14px',
						lineHeight: '1.21em',
						letterSpacing: '-1.79%',
						textTransform: 'none',
						'&:hover': {
							bgcolor: '#F7FAFC',
							border: '1px solid #CBD5E0'
						}
					}}
				>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					disabled={loading}
					fullWidth
					sx={{
						p: '12px 16px',
						bgcolor: '#E9D8FD',
						border: '1px solid #9F7AEA',
						borderRadius: '6px',
						color: '#2D3748',
						fontFamily: 'Inter',
						fontWeight: 600,
						fontSize: '14px',
						lineHeight: '1.21em',
						letterSpacing: '-1.79%',
						textTransform: 'none',
						'&:hover': {
							bgcolor: '#E9D8FD',
							border: '1px solid #9F7AEA'
						}
					}}
				>
					{loading ? 'Saving...' : 'Save'}
				</Button>
			</Box>
		</Dialog>
	)

	return isMobile ? renderMobileDrawer() : renderDesktopModal()
}

// Uppercase section label used to group fields in the desktop modal
const SectionLabel = ({ children }) => (
	<Typography sx={{
		fontFamily: 'Inter',
		fontWeight: 700,
		fontSize: '11px',
		textTransform: 'uppercase',
		letterSpacing: '0.06em',
		color: '#6B46C1',
		mb: '10px'
	}}>
		{children}
	</Typography>
)

// Selectable card toggle used for reminder preferences in the desktop modal
const ReminderCard = ({ icon, label, sub, checked, onToggle }) => (
	<Box
		component="button"
		type="button"
		onClick={onToggle}
		aria-pressed={checked}
		sx={{
			display: 'flex',
			alignItems: 'center',
			gap: '10px',
			flex: 1,
			minWidth: 0,
			textAlign: 'left',
			cursor: 'pointer',
			p: '12px 14px',
			borderRadius: '8px',
			border: checked ? '1.5px solid #9F7AEA' : '1.5px solid #E2E8F0',
			bgcolor: checked ? '#F5F0FE' : '#FFFFFF',
			fontFamily: 'inherit',
			'&:hover': {
				borderColor: '#9F7AEA'
			}
		}}
	>
		<Box sx={{
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			width: 30,
			height: 30,
			flex: 'none',
			borderRadius: '50%',
			bgcolor: checked ? '#E9D8FD' : '#F7FAFC',
			color: checked ? '#6B46C1' : '#A0AEC0'
		}}>
			{icon}
		</Box>
		<Box sx={{ flex: 1, minWidth: 0 }}>
			<Typography sx={{
				fontFamily: 'Inter',
				fontWeight: 600,
				fontSize: '13px',
				color: '#2D3748'
			}}>
				{label}
			</Typography>
			<Typography sx={{
				fontFamily: 'Inter',
				fontWeight: 400,
				fontSize: '12px',
				color: '#718096'
			}}>
				{sub}
			</Typography>
		</Box>
		<Box sx={{
			width: 18,
			height: 18,
			flex: 'none',
			borderRadius: '5px',
			border: checked ? 'none' : '1.5px solid #CBD5E0',
			bgcolor: checked ? '#9F7AEA' : '#FFFFFF',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center'
		}}>
			{checked && (
				<svg width="11" height="11" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M3 8.5l3.2 3L13 4.5" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
				</svg>
			)}
		</Box>
	</Box>
)

// Reusable Form Field Component
const FormField = ({ label, value, onChange, placeholder, type = 'text', select = false, multiline = false, required = false, children }) => (
	<Box sx={{
		display: 'flex',
		flexDirection: 'column',
		alignSelf: 'stretch',
		gap: '4px'
	}}>
		<Typography sx={{
			fontFamily: 'Inter',
			fontWeight: 400,
			fontSize: '12px',
			lineHeight: '1.21em',
			letterSpacing: '-2.083%',
			color: '#2D3748'
		}}>
			{label}
			{required && (
				<Typography component="span" variant="inherit" sx={{ color: '#E53E3E', ml: '2px' }}>
					*
				</Typography>
			)}
		</Typography>
		<TextField
			fullWidth
			type={type}
			value={value}
			onChange={onChange}
			placeholder={placeholder}
			select={select}
			multiline={multiline}
			minRows={multiline ? 3 : undefined}
			variant="outlined"
			sx={{
				'& .MuiOutlinedInput-root': {
					bgcolor: '#FFFFFF',
					borderRadius: '6px',
					'& fieldset': {
						borderColor: '#CBD5E0'
					},
					'&:hover fieldset': {
						borderColor: '#CBD5E0'
					},
					'&.Mui-focused fieldset': {
						borderColor: '#CBD5E0'
					}
				},
				'& .MuiOutlinedInput-input': {
					fontFamily: 'Inter, sans-serif',
					fontWeight: 400,
					fontSize: '14px',
					lineHeight: '1.21em',
					letterSpacing: '-1.786%',
					color: '#2D3748',
					padding: '12px 16px',
					'&::placeholder': {
						color: '#CBD5E0',
						opacity: 1
					}
				}
			}}
		>
			{children}
		</TextField>
	</Box>
)

export default AddEditClientDialog
